/**
 * P3-32: Pacts 面板。
 *
 * 文本每 10 tick 更新一次，对齐旧 PactsWGT.updateInterval。
 */
import { useCallback } from "react";
import { useGameTick } from "../../bridge/useGameTick";
import type { GamePage } from "../../bridge/GameBridge";
import { Panel } from "../../components/Panel";
import { t } from "../../bridge/i18n";
import { PactButton } from "./PactButton";

interface PactsSnapshot {
  visible: boolean;
  pacts: { name: string }[];
  sum: string;
  deficit: string;
  karma: string;
}

function computeSnapshot(game: GamePage | null): PactsSnapshot {
  if (!game) {
    return { visible: false, pacts: [], sum: "", deficit: "", karma: "" };
  }
  const religion = game.religion as {
    getPact?: (name: string) => { researched?: boolean; val?: number } | null;
    getTU?: (name: string) => { val?: number } | null;
    pactsManager?: {
      pacts?: { name: string }[];
      getPactsTextSum?: () => string;
      getPactsTextDeficit?: () => string;
      getPactsTextKarmaPunishment?: () => string;
    };
  };
  const science = game.science as { getPolicy?: (name: string) => { researched?: boolean } | null };
  const fractured = religion.getPact?.("fractured");
  const blackPyramidVal = (game.religion as { getZU?: (n: string) => { val?: number } | null }).getZU?.("blackPyramid")?.val ?? 0;
  const mausoleumVal = religion.getTU?.("mausoleum")?.val ?? 0;
  const visible =
    !(fractured?.researched ?? false) &&
    blackPyramidVal > 0 &&
    (mausoleumVal > 0 || (science.getPolicy?.("radicalXenophobia")?.researched ?? false)) &&
    game.getFeatureFlag("MAUSOLEUM_PACTS");

  const pm = religion.pactsManager;
  return {
    visible,
    pacts: visible ? (pm?.pacts ?? []) : [],
    sum: visible ? (pm?.getPactsTextSum?.() ?? "") : "",
    deficit: visible ? (pm?.getPactsTextDeficit?.() ?? "") : "",
    karma: visible ? (pm?.getPactsTextKarmaPunishment?.() ?? "") : "",
  };
}

function snapshotEq(a: PactsSnapshot, b: PactsSnapshot): boolean {
  if (
    a.visible !== b.visible ||
    a.sum !== b.sum ||
    a.deficit !== b.deficit ||
    a.karma !== b.karma ||
    a.pacts.length !== b.pacts.length
  ) {
    return false;
  }
  for (let i = 0; i < a.pacts.length; i++) {
    if (a.pacts[i].name !== b.pacts[i].name) {
      return false;
    }
  }
  return true;
}

export function PactsPanel() {
  const state = useGameTick(
    useCallback((game: GamePage | null) => computeSnapshot(game), []),
    snapshotEq,
  );

  if (!state.visible) {
    return null;
  }

  return (
    <Panel title={t("religion.panel.pacts.label")}>
      <span id="pactsTextSum" dangerouslySetInnerHTML={{ __html: state.sum }} />
      <div>
        {state.pacts.map((pact) => (
          <PactButton key={pact.name} pactName={pact.name} />
        ))}
      </div>
      <span id="pactsTextDeficit" dangerouslySetInnerHTML={{ __html: state.deficit }} />
      <div>
        <span id="pactsTextKarmaPunishment" dangerouslySetInnerHTML={{ __html: state.karma }} />
      </div>
    </Panel>
  );
}
